import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Portal, Paper, IconButton, Box, Avatar, List, ListItem,
    ListItemAvatar, ListItemText, Divider, InputBase, Typography,
    Menu, MenuItem, TextField, CircularProgress, Modal, Backdrop, Fade,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MinimizeIcon from '@mui/icons-material/Minimize';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SendIcon from '@mui/icons-material/Send';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import dirtyjson from 'dirty-json';
import PropTypes from 'prop-types';

import { GroupChannelHandler } from '@sendbird/chat/groupChannel';
import { useSendbirdStateContext } from '@sendbird/uikit-react';
import { useChat } from '../../contexts/ChatContext';
import { useJobs } from 'services/job';
import { MessageType } from '@sendbird/chat/message';
import { useSendBirdReady } from 'services/sendBird/sendBirdService';
import ReadReceiptIcon from '../Icons/ReadReceiptIcons';
import { getReadReceiptStatus } from '../../helpers/MessageStatus';

const dockStyle = {
    position: 'fixed',
    bottom: 12,
    right: 12,
    width: 900,
    height: 520,
    zIndex: 1300,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
};

const HEADER_H = 69; //nice

const headerLeftSx = {
    height: HEADER_H,
    display: 'flex',
    alignItems: 'center',
    px: 2,
    borderBottom: '1px solid',
    borderColor: 'divider',
    flexShrink: 0,
};

const headerRightSx = {
    height: HEADER_H,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: 2,
    borderBottom: '1px solid',
    borderColor: 'divider',
    flexShrink: 0,
};

const extractChannelUrlsFromJob = (job) => {
    if (!job) return [];
    let urls = [];

    if(job?.chat?.channels && typeof job.chat.channels === 'object') {
        Object.values(job.chat.channels).forEach((item) => {
            urls.push(item.channel_url);
        });
    } else if(job?.chat?.channels && Array.isArray(job.chat.channels)) {
        job.chat.channels.forEach((item) => {
            urls.push(item.channel_url);
        });
    } else {
        console.warn('Unexpected channel format:', job.chat.channels);
    }
    return urls;
};

const isUserMsg = (msg) =>
    !!msg && msg.messageType === MessageType.USER;

const isFileMsg = (msg) =>
    !!msg && (msg.messageType === MessageType.FILE || (typeof msg.url === 'string' && msg.messageType !== MessageType.USER));

const isAdminMsg = (msg) =>
    !!msg && msg.messageType === MessageType.ADMIN;

const isImageMsg = (msg) => {
    if (!isFileMsg(msg)) return false;

    // Check multiple possible properties for MIME type and file name
    const mimeType = msg.type || msg.mimeType || msg.contentType || '';
    const fileName = msg.name || msg.fileName || '';

    // More aggressive image detection
    const isImageByMimeType = mimeType.startsWith('image/');
    const isImageByExtension = /\.(jpg|jpeg|png|gif|bmp|webp|svg|jfif)$/i.test(fileName);

    // Also check if the URL suggests it's an image (common image hosting patterns)
    const isImageByUrl = msg.url && /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(msg.url);

    return isImageByMimeType || isImageByExtension || isImageByUrl;
};

const getMessageText = (msg) => {
    if (isUserMsg(msg) || isAdminMsg(msg)) return msg.message || '';
    if (isFileMsg(msg)) return msg.name || 'Attachment';
    return '';
};
const toValidDate = (ts) => {
    const n = Number(ts);
    return Number.isFinite(n) && n > 0 ? new Date(n) : new Date();
};

const fmtDay = (ts) =>
    new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
        .format(toValidDate(ts));

const fmtTime = (ts) =>
    new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' })
        .format(toValidDate(ts));

const ellipsize = (t, n = 64) => (t?.length > n ? `${t.slice(0, n - 1)}…` : t || '');

const getTitle = (channel, currentUserId) => {
    if (!channel) return 'Channel';
    if (channel.name && channel.name.trim()) return channel.name;
    const others = (channel.members || []).filter((m) => m.userId !== currentUserId);
    return others.map((m) => m.nickname || m.userId).join(', ') || 'Channel';
};

function useChannels(sdk, urls) {
    const [channels, setChannels] = useState([]);

    useEffect(() => {
        let handlerId;

        async function load() {
            if (!sdk || !urls?.length) {
                setChannels([]);
                return;
            }

            const wanted = Array.from(new Set(
                urls
                    .map((u) => (typeof u === 'string' ? u.trim() : ''))
                    .filter((u) => !!u),
            ));
            if (wanted.length === 0) {
                setChannels([]);
                return;
            }

            let fetched = [];
            try {
                const q = sdk.groupChannel.createMyGroupChannelListQuery({
                    channelUrlsFilter: wanted,
                    includeEmpty: true,
                    limit: Math.max(20, wanted.length),
                    order: 'latest_last_message',
                });
                fetched = await q.next();
            } catch (e) {
                console.error('Failed to fetch channels:', e);
            }

            const have = new Set(fetched.map((c) => c.url));
            const missing = wanted.filter((u) => !have.has(u));
            if (missing.length) {
                const results = await Promise.allSettled(
                    missing.map((u) => sdk.groupChannel.getChannel(u)),
                );
                results.forEach((r) => {
                    if (r.status === 'fulfilled' && r.value) fetched.push(r.value);
                });
            }

            fetched.sort((a, b) => (b.lastMessage?.createdAt || 0) - (a.lastMessage?.createdAt || 0));
            setChannels(fetched);

            const handler = new GroupChannelHandler();
            handler.onMessageReceived = (c) => {
                if (!wanted.includes(c.url)) return;
                setChannels((prev) => {
                    const next = prev.map((x) => (x.url === c.url ? c : x));
                    next.sort((a, b) => (b.lastMessage?.createdAt || 0) - (a.lastMessage?.createdAt || 0));
                    return next;
                });
            };
            handler.onChannelChanged = (c) => {
                if (!wanted.includes(c.url)) return;
                setChannels((prev) => prev.map((x) => (x.url === c.url ? c : x)));
            };

            handlerId = `inbox-${Date.now()}-${Math.random().toString(36).slice(2)}`;
            sdk.groupChannel.addGroupChannelHandler(handlerId, handler);
        }

        load();

        return () => {
            if (handlerId && sdk) sdk.groupChannel.removeGroupChannelHandler(handlerId);
        };
    }, [sdk, JSON.stringify(urls)]);

    const optimisticMarkRead = (url) => {
        setChannels((prev) =>
            prev.map((c) => {
                if (c.url !== url) return c;
                try { c.unreadMessageCount = 0; } catch { console.warn('Failed to mark read'); }
                return c;
            }));
    };

    return { channels, optimisticMarkRead };
}

function useMessages(sdk, channel) {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const scrollRef = useRef(null);

    const scrollToBottom = (smooth = false) => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    };

    useEffect(() => {
        // eslint-disable-next-line no-unused-vars
        let disposed = false;
        let handlerId;

        async function init() {
            if (!sdk || !channel) {
                setMessages([]);
                return;
            }

            setLoading(true);
            try {
                const q = channel.createPreviousMessageListQuery({
                    limit: 100,
                    reverse: false,
                    includeReactions: true,
                });

                let all = [];
                while (true) {
                    const batch = await q.load();
                    all = all.concat(batch);
                    if (!q.hasMore) break;
                }

                all.sort((a, b) => Number(a?.createdAt || 0) - Number(b?.createdAt || 0));

                setMessages(all);

                requestAnimationFrame(() => {
                    const el = scrollRef.current;
                    if (el) el.scrollTop = el.scrollHeight;
                });
            } finally {
                setLoading(false);
            }

            const handler = new GroupChannelHandler();
            handler.onMessageReceived = (c, m) => {
                if (c.url !== channel.url) return;
                setMessages((prev) => [...prev, m]);
                requestAnimationFrame(() => {
                    const el = scrollRef.current;
                    if (el) el.scrollTop = el.scrollHeight;
                });
            };
            handler.onMessageUpdated = (c, m) => {
                if (c.url !== channel.url) return;
                setMessages((prev) => prev.map((x) => (x.messageId === m.messageId ? m : x)));
            };
            handler.onMessageDeleted = (c, id) => {
                if (c.url !== channel.url) return;
                setMessages((prev) => prev.filter((x) => x.messageId !== id));
            };

            handlerId = `msgs-${Date.now()}-${Math.random().toString(36).slice(2)}`;
            sdk.groupChannel.addGroupChannelHandler(handlerId, handler);
        }

        init();

        return () => {
            disposed = true;
            if (handlerId && sdk) sdk.groupChannel.removeGroupChannelHandler(handlerId);
        };
    }, [sdk, channel?.url]);

    useEffect(() => {
        requestAnimationFrame(() => scrollToBottom(true));
    }, [messages.length, channel?.url]);

    return { messages, setMessages, scrollRef, loading };
}

export default function ChatDock({ channelUrls = [] }) {
    const { open, minimized, channel: ctxChannel, close, minimize, restore } = useChat();
    const [relatedUrls, setRelatedUrls] = useState([]);
    const jobsContext = useJobs();
    const chatDockRef = useRef(null);

    // Message editing state
    const [editingMessageId, setEditingMessageId] = useState(null);
    const [editText, setEditText] = useState('');
    const [editMenuAnchor, setEditMenuAnchor] = useState(null);
    const [editingMessage, setEditingMessage] = useState(null);

    const ready = useSendBirdReady();

    if (!ready) return null;

    const sbCtx = useSendbirdStateContext();
    const sdk = sbCtx?.stores?.sdkStore?.sdk;
    const currentUserId = sdk?.currentUser?.userId;

    const contextUrl = useMemo(() => {
        if (!ctxChannel) return null;
        return typeof ctxChannel === 'string' ? ctxChannel : (ctxChannel.url ?? null);
    }, [ctxChannel]);

    const seedUrls = useMemo(() => {
        const base = [];
        if (contextUrl) base.push(contextUrl);
        if (relatedUrls?.length) {
            base.push(...relatedUrls);
        } else if (channelUrls?.length) {
            base.push(...channelUrls);
        }
        return Array.from(new Set(base.filter(Boolean)));
    }, [contextUrl, relatedUrls, channelUrls]);

    const { channels, optimisticMarkRead } = useChannels(sdk, seedUrls);

    const [activeUrl, setActiveUrl] = useState(contextUrl || channelUrls?.[0] || null);

    const requestedUrl = ctxChannel?.url || null;
    const userPickedRef = React.useRef(false);

    const pickChannel = React.useCallback((url) => {
        userPickedRef.current = true;
        setActiveUrl(url);
        optimisticMarkRead(url);
        const ch = channels.find(c => c.url === url);
        ch?.markAsRead?.();
    }, [channels, optimisticMarkRead]);

    React.useEffect(() => {
        userPickedRef.current = false;
    }, [requestedUrl]);

    React.useEffect(() => {
        if (!channels || channels.length === 0) return;

        if (!userPickedRef.current && requestedUrl && channels.some(c => c.url === requestedUrl)) {
            setActiveUrl(requestedUrl);
            return;
        }

        setActiveUrl(prev =>
            prev && channels.some(c => c.url === prev)
                ? prev
                : channels[0].url);
    }, [channels, requestedUrl]);

    const active = useMemo(() => channels.find((c) => c.url === activeUrl) || null, [channels, activeUrl]);

    const channelName = getTitle(active, currentUserId);

    const activeData = useMemo(() => {
        try {
            return dirtyjson.parse(active?.data || '{}');
        } catch {
            return {};
        }
    }, [active?.data]);

    const leftHeaderLabel = (typeof activeData?.vin === 'string' && activeData.vin.trim())
        ? activeData.vin.trim().toUpperCase()
        : 'Channels';

    const { messages, setMessages, scrollRef } = useMessages(sdk, active);

    useEffect(() => {
        if (!open || !active) return;
        active.markAsRead?.();
        optimisticMarkRead(active.url);
    }, [open, active?.url]);

    useEffect(() => {
        if (!open || !active || messages.length === 0) return;
        active.markAsRead?.();
        optimisticMarkRead(active.url);
    }, [open, active?.url, messages.length]);

    const lastCtxUrlRef = useRef(null);
    useEffect(() => {
        if (!contextUrl || lastCtxUrlRef.current === contextUrl) return;
        lastCtxUrlRef.current = contextUrl;
        setRelatedUrls([]);
    }, [contextUrl]);

    const lastReadTs = useMemo(() => {
        if (!active || !currentUserId) return 0;
        const me = active.members?.find((m) => m.userId === currentUserId);
        return me?.lastSeenAt || 0;
    }, [active, currentUserId]);

    const unreadStart = useMemo(() => {
        if (!lastReadTs || messages.length === 0) return -1;
        return messages.findIndex((m) => Number(m?.createdAt) > 0 && m.createdAt > lastReadTs);
    }, [messages, lastReadTs]);

    const withBreaks = useMemo(() => {
        const out = [];
        let lastDay = '';
        messages.forEach((m, i) => {
            const ts = Number(m?.createdAt) > 0
                ? m.createdAt
                : (Number(m?.updatedAt) > 0 ? m.updatedAt : Date.now());

            const day = fmtDay(ts);
            if (day !== lastDay) {
                out.push({ t: 'day', k: `d-${ts}-${i}`, label: day });
                lastDay = day;
            }
            if (unreadStart === i) {
                const since = new Intl.DateTimeFormat(undefined, {
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                }).format(toValidDate(lastReadTs));
                const remaining = messages.length - i;
                out.push({
                    t: 'unread',
                    k: `u-${i}`,
                    label: `${remaining} new message${remaining > 1 ? 's' : ''} since ${since}`,
                });
            }
            out.push({ t: 'msg', k: m.messageId || `m-${i}`, m, ts });
        });
        return out;
    }, [messages, unreadStart, lastReadTs]);

    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    // Image modal state
    const [imageModalOpen, setImageModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);

    // Message rendering function
    const renderMessageBody = (msg) => {
        if (isUserMsg(msg) || isAdminMsg(msg)) {
            return (
                <Typography
                    variant='body2'
                    component='div'
                    sx={{
                        whiteSpace: 'pre-wrap',
                        lineHeight: '20px',
                        fontSize: '14px',
                        fontWeight: 400,
                        letterSpacing: 0,
                    }}
                >
                    {msg.message || ''}
                </Typography>
            );
        }
        if (isFileMsg(msg)) {
            if (isImageMsg(msg)) {
                // Clean image display without filename text
                return (
                    <img
                        src={msg.url}
                        alt={msg.name || 'Image'}
                        style={{
                            width: '200px',
                            height: '150px',
                            objectFit: 'contain',
                            borderRadius: '8px',
                            cursor: 'pointer',
                        }}
                        onClick={() => handleImageClick(msg.url, msg.name)}
                    />
                );
            }
            return (
                <Typography
                    variant='body2'
                    component='div'
                    sx={{
                        whiteSpace: 'pre-wrap',
                        lineHeight: '20px',
                        fontSize: '14px',
                        fontWeight: 400,
                        letterSpacing: 0,
                    }}
                >
                    <a href={msg.url} target='_blank' rel='noreferrer'>
                        {msg.name || msg.fileName || 'Attachment'}
                    </a>
                </Typography>
            );
        }
        return (
            <Typography
                variant='body2'
                component='div'
                sx={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: '20px',
                    fontSize: '14px',
                    fontWeight: 400,
                    letterSpacing: 0,
                }}
            >
                [Unsupported message]
            </Typography>
        );
    };
    const send = () => {
        const trimmed = text.trim();
        if (!trimmed || !sdk || !active || sending) return;

        const scrollToBottom = (smooth = false) => {
            const el = scrollRef.current;
            if (!el) return;
            el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
        };

        setSending(true);
        setText('');

        try {
            const req = active.sendUserMessage({ message: trimmed });

            req.onPending((pendingMsg) => {
                setMessages((prev) => [...prev, pendingMsg]);
                requestAnimationFrame(() => scrollToBottom(true));
            });

            req.onSucceeded((succeededMsg) => {
                setMessages((prev) => prev.map((m) => (m?.reqId && m.reqId === succeededMsg.reqId ? succeededMsg : m)));
                active.markAsRead?.();
                requestAnimationFrame(() => scrollToBottom(true));
                setSending(false);
            });

            req.onFailed((failedMsg) => {
                setMessages((prev) => prev.map((m) => (m?.reqId && m.reqId === failedMsg.reqId ? { ...failedMsg } : m)));
                requestAnimationFrame(() => scrollToBottom(true));
                setSending(false);
            });

        } catch (e) {
            console.error('Failed to send message:', e);
            setSending(false);
        }
    };

    const handleFileUpload = async (file) => {
        if (!file || !sdk || !active || uploading) return;

        const scrollToBottom = (smooth = false) => {
            const el = scrollRef.current;
            if (!el) return;
            el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
        };

        setUploading(true);

        try {
            const req = active.sendFileMessage({
                file: file,
                fileName: file.name,
                fileSize: file.size,
                mimeType: file.type,
            });

            req.onPending((pendingMsg) => {
                setMessages((prev) => [...prev, pendingMsg]);
                requestAnimationFrame(() => scrollToBottom(true));
            });

            req.onSucceeded((succeededMsg) => {
                setMessages((prev) => prev.map((m) => (m?.reqId && m.reqId === succeededMsg.reqId ? succeededMsg : m)));
                active.markAsRead?.();
                requestAnimationFrame(() => scrollToBottom(true));
                setUploading(false);
            });

            req.onFailed((failedMsg) => {
                setMessages((prev) => prev.map((m) => (m?.reqId && m.reqId === failedMsg.reqId ? { ...failedMsg } : m)));
                requestAnimationFrame(() => scrollToBottom(true));
                setUploading(false);
            });

        } catch (e) {
            console.error('Failed to upload file:', e);
            setUploading(false);
        }
    };

    const handleAttachmentClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        if (file) {
            handleFileUpload(file);
        }
        // Reset the input so the same file can be selected again
        event.target.value = '';
    };

    const handleImageClick = (imageUrl, imageName) => {
        setSelectedImage({ url: imageUrl, name: imageName });
        setImageModalOpen(true);
    };

    const handleImageModalClose = () => {
        setImageModalOpen(false);
        setSelectedImage(null);
    };

    const getJobById = async (jobId) => {
        if (!jobId) return null;

        const cached = jobsContext?.state?.jobs?.find((j) => String(j.id) === String(jobId));
        if (cached) return cached;

        if (jobsContext?.actions?.refreshJob) {
            const res = await jobsContext.actions.refreshJob(jobId);
            return res?.payload ?? res ?? null;
        }

        return null;
    };

    // Message editing functions
    const handleEditClick = (message, event) => {
        event.stopPropagation();
        setEditingMessage(message);
        setEditText(message.message || '');
        setEditingMessageId(message.messageId);
        setEditMenuAnchor(null);
    };

    const handleEditCancel = () => {
        setEditingMessageId(null);
        setEditText('');
        setEditingMessage(null);
    };

    const handleEditSave = async () => {
        if (!editingMessage || !editText.trim() || !active) return;

        try {
            await active.updateUserMessage(editingMessage.messageId, {
                message: editText.trim(),
            });

            // Update the local messages state to reflect the edit
            setMessages(prevMessages =>
                prevMessages.map(msg =>
                    msg.messageId === editingMessage.messageId
                        ? { ...msg, message: editText.trim(), updatedAt: Date.now() }
                        : msg));

            handleEditCancel();
        } catch (error) {
            console.error('Failed to edit message:', error);
        }
    };

    const handleEditMenuOpen = (event, message) => {
        event.stopPropagation();
        setEditMenuAnchor(event.currentTarget);
        setEditingMessage(message);
    };

    const handleEditMenuClose = () => {
        // Use setTimeout to ensure the menu closes properly before clearing the anchor
        setTimeout(() => {
            setEditMenuAnchor(null);
            setEditingMessage(null);
        }, 0);
    };

    const handleDeleteClick = async (message, event) => {
        event.stopPropagation();
        if (!message || !active) return;

        // Validate message object before attempting deletion
        if (!message.messageId || !message.messageType) {
            console.error('Invalid message object for deletion:', message);
            return;
        }

        // Close the menu first to prevent anchorEl issues
        setEditMenuAnchor(null);
        setEditingMessage(null);

        try {
            // Ensure the message is a valid Sendbird message object
            if (typeof message.messageId !== 'number' && typeof message.messageId !== 'string') {
                console.error('Invalid messageId:', message.messageId);
                return;
            }

            // Try the correct Sendbird API method
            await active.deleteMessage(message);

            // Update the local messages state to remove the deleted message
            setMessages(prevMessages =>
                prevMessages.filter(msg => msg.messageId !== message.messageId));
        } catch (error) {
            console.error('Failed to delete message:', error);

            // If deletion fails, still remove from local state to prevent UI inconsistency
            // This handles cases where the message was already deleted or the channel state changed
            setMessages(prevMessages =>
                prevMessages.filter(msg => msg.messageId !== message.messageId));
        }
    };

    useEffect(() => {
        let mounted = true;

        (async () => {
            const jobId = activeData?.job_id ?? activeData?.jobId ?? activeData?.jobID;
            const base = active?.url ? [active.url] : [];

            if (!jobId) {
                if (mounted) setRelatedUrls(base);
                return;
            }

            const job = await getJobById(jobId);
            const fromJob = extractChannelUrlsFromJob(job);
            const merged = Array.from(new Set([...base, ...(fromJob || [])]));

            if (mounted) setRelatedUrls(merged);
        })();

        return () => { mounted = false; };
    }, [active?.url, activeData?.job_id, activeData?.jobId, activeData?.jobID]);

    // Handle clicks outside ChatDock to minimize it
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Don't minimize if the edit menu is open or image modal is open
            if (open && chatDockRef.current && !chatDockRef.current.contains(event.target) && !editMenuAnchor && !imageModalOpen) {
                minimize();
            }
        };

        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open, minimize, editMenuAnchor, imageModalOpen]);

    if (!open && !minimized) return null;
    if (!ready) return null;

    return (
        <Portal container={document.body}>
            {minimized && active && (
                <Box sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1800 }}>
                    <Paper
                        onClick={restore}
                        role='button'
                        aria-label='Open chat'
                        sx={(theme) => ({
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            minWidth: 320,
                            maxWidth: 420,
                            px: 2.25,
                            py: 1.25,
                            borderRadius: '16px',
                            cursor: 'pointer',
                            backgroundColor: theme.palette.background.paper,
                            boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                            border: `1px solid ${theme.palette.grey[100]}`,
                        })}
                        elevation={0}
                    >
                        <Box
                            sx={{
                                fontWeight: 800,
                                fontSize: 18,
                                letterSpacing: 0.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                pr: 2,
                            }}
                            title={leftHeaderLabel}
                        >
                            {leftHeaderLabel}
                        </Box>

                        <Box
                            sx={(theme) => ({
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                background: theme.palette.grey[100],
                                display: 'grid',
                                placeItems: 'center',
                                flexShrink: 0,
                            })}
                        >
                            <ExpandLessIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                        </Box>
                    </Paper>
                </Box>
            )}

            {open && active && (
                <Paper ref={chatDockRef} elevation={6} sx={dockStyle}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '300px 1fr', flex: 1, minHeight: 0 }}>
                        <Box
                            sx={{
                                borderRight: '1px solid',
                                borderColor: 'divider',
                                display: 'flex',
                                flexDirection: 'column',
                                minWidth: 0,
                            }}
                        >
                            <Box sx={headerLeftSx}>
                                <Typography
                                    variant='subtitle1'
                                    sx={{ fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    title={leftHeaderLabel}
                                >
                                    {leftHeaderLabel}
                                </Typography>
                            </Box>
                            <List sx={{ py: 0, overflowY: 'auto' }}>
                                {(channels || []).map((c) => {
                                    const selected = c.url === activeUrl;
                                    const title = getTitle(c, currentUserId);
                                    const preview = getMessageText(c.lastMessage);
                                    const unread = c.unreadMessageCount ?? 0;
                                    const lastTs = c.lastMessage?.createdAt;
                                    const timeLabel = lastTs ? fmtDay(lastTs) : '';

                                    return (
                                        <ListItem
                                            key={c.url}
                                            onClick={() => pickChannel(c.url)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') pickChannel(c.url);
                                            }}
                                            tabIndex={0}
                                            role='option'
                                            aria-selected={selected}
                                            selected={selected}
                                            sx={(t) => ({
                                                position: 'relative',
                                                alignItems: 'flex-start',
                                                cursor: 'pointer',
                                                py: 1.25,
                                                pr: 1.25,
                                                '&:hover': { backgroundColor: 'action.hover' },
                                                ...(selected && {
                                                    backgroundColor: t.palette.mode === 'dark'
                                                        ? 'rgba(25,118,210,0.18)'
                                                        : 'rgba(25,118,210,0.12)',
                                                }),
                                            })}
                                        >
                                            {selected && (
                                                <Box sx={{
                                                    position: 'absolute',
                                                    left: 0,
                                                    top: 0,
                                                    bottom: 0,
                                                    width: 4,
                                                    bgcolor: 'primary.main',
                                                    borderTopRightRadius: 4,
                                                    borderBottomRightRadius: 4,
                                                }} />
                                            )}

                                            <ListItemAvatar sx={{ mt: 0.25, mr: 1.5 }}>
                                                <Avatar>{title.slice(0, 1).toUpperCase()}</Avatar>
                                            </ListItemAvatar>

                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                                        <Typography sx={{
                                                            fontWeight: 700,
                                                            pr: 1,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            color: selected ? 'primary.main' : 'text.primary',
                                                            flex: 1,
                                                        }}>
                                                            {title}
                                                        </Typography>
                                                        <Typography variant='caption' color='text.secondary' sx={{ flexShrink: 0 }}>
                                                            {timeLabel}
                                                        </Typography>
                                                    </Box>
                                                }
                                                secondary={
                                                    <Typography variant='body2' color='text.secondary'>{ellipsize(preview, 80)}</Typography>
                                                }
                                                sx={{ my: 0 }}
                                            />

                                            {unread > 0 && (
                                                <Box sx={{ ml: 1, alignSelf: 'center', flexShrink: 0 }}>
                                                    <Box sx={{
                                                        minWidth: 22,
                                                        height: 22,
                                                        px: 0.75,
                                                        borderRadius: '999px',
                                                        backgroundColor: 'primary.main',
                                                        color: 'primary.contrastText',
                                                        fontSize: 12,
                                                        fontWeight: 700,
                                                        display: 'grid',
                                                        placeItems: 'center',
                                                    }}>
                                                        {unread > 99 ? '99+' : unread}
                                                    </Box>
                                                </Box>
                                            )}
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
                            <Box
                                sx={headerRightSx}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        px: 2,
                                        py: 1.25,
                                    }}
                                >
                                    <Avatar sx={{ width: 28, height: 28 }}>{channelName.slice(0, 1).toUpperCase()}</Avatar>
                                    <Typography
                                        sx={{
                                            fontWeight: 700,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            fontSize: '18px',
                                        }}
                                    >
                                        {channelName}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        px: 2,
                                        py: 1.25,
                                    }}
                                >
                                    <IconButton size='small' onClick={minimize} aria-label='Minimize'>
                                        <MinimizeIcon fontSize='small' />
                                    </IconButton>
                                    <IconButton size='small' onClick={close} aria-label='Close'>
                                        <CloseIcon fontSize='small' />
                                    </IconButton>
                                </Box>
                            </Box>

                            <Box
                                ref={scrollRef}
                                sx={{
                                    flex: 1,
                                    overflowY: 'auto',
                                    p: 2,
                                    backgroundColor: (t) => t.palette.background.paper,
                                    minHeight: 0,
                                }}
                            >
                                {(() => {
                                    const items = [];
                                    withBreaks.forEach((it) => {
                                        if (it.t === 'day') {
                                            items.push(
                                                <Box key={it.k} sx={{ my: 1.5, textAlign: 'center', position: 'relative' }}>
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: '50%',
                                                            left: 0,
                                                            right: 0,
                                                            height: '1px',
                                                            backgroundColor: 'divider',
                                                            zIndex: 0,
                                                        }}
                                                    />
                                                    <Typography
                                                        variant='caption'
                                                        sx={{
                                                            color: 'text.secondary',
                                                            fontWeight: 700,
                                                            backgroundColor: 'background.default',
                                                            px: 2,
                                                            position: 'relative',
                                                            zIndex: 1,
                                                        }}
                                                    >
                                                        {it.label}
                                                    </Typography>
                                                </Box>,
                                            );
                                            return;
                                        }
                                        if (it.t === 'unread') {
                                            items.push(
                                                <Paper
                                                    key={it.k}
                                                    elevation={0}
                                                    sx={(t) => ({
                                                        my: 1.25,
                                                        mx: 'auto',
                                                        px: 1.75,
                                                        py: 0.75,
                                                        borderRadius: '999px',
                                                        width: 'fit-content',
                                                        fontSize: 13,
                                                        fontWeight: 700,
                                                        color: t.palette.getContrastText(t.palette.primary.main),
                                                        backgroundColor: t.palette.primary.main,
                                                    })}
                                                >
                                                    {it.label}
                                                </Paper>,
                                            );
                                            return;
                                        }
                                        const m = it.m;
                                        const mine = m?.sender?.userId === currentUserId;
                                        const isEditing = editingMessageId === m.messageId;
                                        items.push(
                                            <Box
                                                key={it.k}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: mine ? 'flex-end' : 'flex-start',
                                                    alignItems: 'flex-end',
                                                    gap: 1,
                                                    my: 0.5,
                                                }}
                                            >
                                                {mine && (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                            minWidth: 58,
                                                            maxWidth: 58,
                                                            justifyContent: 'flex-end',
                                                            flex: 1,
                                                        }}
                                                    >
                                                        <ReadReceiptIcon
                                                            status={getReadReceiptStatus(m, currentUserId, active)}
                                                            size={14}
                                                        />
                                                        <Typography
                                                            variant='caption'
                                                            sx={{ color: 'text.disabled' }}
                                                        >
                                                            {fmtTime(it.ts)}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {isEditing ? (
                                                    // Edit mode
                                                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, flex: 1 }}>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: 1,
                                                            flex: 1,
                                                            width: '100%',
                                                            minWidth: 0,
                                                        }}>
                                                            <TextField
                                                                value={editText}
                                                                onChange={(e) => setEditText(e.target.value)}
                                                                multiline
                                                                maxRows={4}
                                                                variant='outlined'
                                                                size='small'
                                                                fullWidth
                                                                sx={{
                                                                    width: '100%',
                                                                    minWidth: 0,
                                                                    '& .MuiOutlinedInput-root': {
                                                                        borderRadius: mine ? '16px 16px 6px 16px' : '16px 16px 16px 6px',
                                                                        backgroundColor: mine ? 'primary.main' : '#EEEEEE',
                                                                        color: mine ? 'primary.contrastText' : 'text.primary',
                                                                        width: '100%',
                                                                        minWidth: 0,
                                                                        '& fieldset': {
                                                                            borderColor: 'transparent',
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor: 'transparent',
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: 'transparent',
                                                                        },
                                                                    },
                                                                    '& .MuiOutlinedInput-input': {
                                                                        color: mine ? 'primary.contrastText' : 'text.primary',
                                                                        fontSize: '14px',
                                                                        padding: '8px 12px',
                                                                    },
                                                                }}
                                                                autoFocus
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                                        e.preventDefault();
                                                                        handleEditSave();
                                                                    } else if (e.key === 'Escape') {
                                                                        handleEditCancel();
                                                                    }
                                                                }}
                                                            />
                                                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                                <IconButton
                                                                    size='small'
                                                                    onClick={handleEditSave}
                                                                    disabled={!editText.trim()}
                                                                    sx={{
                                                                        backgroundColor: 'success.main',
                                                                        color: 'white',
                                                                        '&:hover': { backgroundColor: 'success.dark' },
                                                                        '&:disabled': { backgroundColor: 'grey.400' },
                                                                    }}
                                                                >
                                                                    <CheckIcon fontSize='small' />
                                                                </IconButton>
                                                                <IconButton
                                                                    size='small'
                                                                    onClick={handleEditCancel}
                                                                    sx={{
                                                                        backgroundColor: 'error.main',
                                                                        color: 'white',
                                                                        '&:hover': { backgroundColor: 'error.dark' },
                                                                    }}
                                                                >
                                                                    <CancelIcon fontSize='small' />
                                                                </IconButton>
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                ) : (
                                                    // Normal message display
                                                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                                                        <Paper
                                                            elevation={0}
                                                            sx={{
                                                                px: isImageMsg(m) ? 0.75 : 1.75,
                                                                py: isImageMsg(m) ? 0.75 : 1.75,
                                                                borderRadius: mine ? '16px 16px 6px 16px' : '16px 16px 16px 6px',
                                                                backgroundColor: mine ? 'primary.main' : '#EEEEEE',
                                                                color: mine ? 'primary.contrastText' : 'text.primary',
                                                                wordBreak: 'break-word',
                                                            }}
                                                        >
                                                            {renderMessageBody(m)}
                                                            {m.updatedAt && m.updatedAt !== m.createdAt ? (
                                                                <Typography
                                                                    component='span'
                                                                    variant='caption'
                                                                    sx={{
                                                                        display: 'block',
                                                                        fontSize: '11px',
                                                                        opacity: 0.7,
                                                                        marginTop: 0.5,
                                                                        fontStyle: 'italic',
                                                                        color: mine ? 'primary.contrastText' : 'text.primary',
                                                                    }}
                                                                >
                                                                    (edited)
                                                                </Typography>
                                                            ) : null}
                                                        </Paper>

                                                        {/* Edit menu button for user's messages */}
                                                        {mine && (
                                                            <IconButton
                                                                size='small'
                                                                onClick={(e) => handleEditMenuOpen(e, m)}
                                                                sx={{
                                                                    backgroundColor: 'rgba(255,255,255,0.9)',
                                                                    boxShadow: 1,
                                                                    opacity: 1,
                                                                    width: 24,
                                                                    height: 24,
                                                                    '&:hover': {
                                                                        backgroundColor: 'rgba(255,255,255,1)',
                                                                    },
                                                                }}
                                                            >
                                                                <MoreVertIcon fontSize='small' />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                )}

                                                {!mine && (
                                                    <Typography
                                                        variant='caption'
                                                        sx={{ color: 'text.disabled', minWidth: 58, textAlign: 'left' }}
                                                    >
                                                        {fmtTime(it.ts)}
                                                    </Typography>
                                                )}
                                            </Box>,
                                        );
                                    });
                                    return items;
                                })()}
                            </Box>

                            {/* Edit Menu */}
                            <Menu
                                anchorEl={editMenuAnchor && editMenuAnchor.isConnected ? editMenuAnchor : null}
                                open={Boolean(editMenuAnchor && editMenuAnchor.isConnected)}
                                onClose={handleEditMenuClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                disableAutoFocusItem
                                disableEnforceFocus
                                disableRestoreFocus
                            >
                                <MenuItem
                                    onClick={(e) => handleEditClick(editingMessage, e)}
                                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                >
                                    <EditIcon fontSize='small' />
                                    Edit message
                                </MenuItem>
                                <MenuItem
                                    onClick={(e) => handleDeleteClick(editingMessage, e)}
                                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                >
                                    <DeleteIcon fontSize='small' />
                                    Delete message
                                </MenuItem>
                            </Menu>

                            <Divider />

                            <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <IconButton
                                    size='small'
                                    onClick={handleAttachmentClick}
                                    disabled={!active || uploading}
                                    sx={{
                                        opacity: uploading ? 0.5 : 1,
                                    }}
                                >
                                    {uploading ? (
                                        <CircularProgress size={16} />
                                    ) : (
                                        <AttachFileIcon fontSize='small' />
                                    )}
                                </IconButton>
                                <input
                                    type='file'
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    style={{ display: 'none' }}
                                    accept='image/*,video/*,.pdf,.doc,.docx,.txt'
                                />
                                <Paper
                                    variant='outlined'
                                    sx={{
                                        flex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        borderRadius: '24px',
                                        px: 1,
                                        py: 0.25,
                                    }}
                                >
                                    <InputBase
                                        value={text}
                                        onChange={(e) => setText(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                send();
                                            }
                                        }}
                                        placeholder='Enter message'
                                        sx={{ flex: 1, px: 1, py: 0.75 }}
                                        multiline
                                        maxRows={4}
                                    />
                                    <IconButton onClick={send} disabled={!text.trim() || !active || sending || uploading}>
                                        <SendIcon />
                                    </IconButton>
                                </Paper>
                            </Box>
                        </Box>
                    </Box>
                </Paper>
            )}

            {/* Image Modal */}
            <Modal
                open={imageModalOpen}
                onClose={handleImageModalClose}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={imageModalOpen}>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '90vw',
                            height: '90vh',
                            maxWidth: '1200px',
                            maxHeight: '800px',
                            backgroundColor: 'rgba(0, 0, 0, 0.9)',
                            borderRadius: '8px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            outline: 'none',
                        }}
                    >
                        {/* Close button */}
                        <IconButton
                            onClick={handleImageModalClose}
                            sx={{
                                position: 'absolute',
                                top: 16,
                                right: 16,
                                color: 'white',
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>

                        {/* Image */}
                        {selectedImage && (
                            <img
                                src={selectedImage.url}
                                alt={selectedImage.name || 'Image'}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain',
                                }}
                            />
                        )}

                        {/* Image name */}
                        {selectedImage?.name && (
                            <Typography
                                variant='body2'
                                sx={{
                                    color: 'white',
                                    mt: 2,
                                    textAlign: 'center',
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    px: 2,
                                    py: 1,
                                    borderRadius: '4px',
                                }}
                            >
                                {selectedImage.name}
                            </Typography>
                        )}
                    </Box>
                </Fade>
            </Modal>
        </Portal>
    );
}

ChatDock.propTypes = {
    channelUrls: PropTypes.arrayOf(PropTypes.string),
};
